import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Stack,
    Typography,
    Autocomplete,
    Chip,
    CircularProgress,
    Divider,
    InputAdornment,
    SelectChangeEvent,
    Link as MuiLink, // For Back link
    Alert,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
    useDataProvider,
    useGetIdentity,
    useRedirect,
    useNotify,
    Identifier,
    useRecordContext, // Keep for potential helpers, maybe
    RaRecord, // Import RaRecord if needed by Quote
    ReferenceField, // Add ReferenceField import
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom'; // For Back link
import { DialogCloseButton } from '../misc/DialogCloseButton';
import { Account, Contact, Quote, QuoteLine, Sale } from '../types';
import { useConfigurationContext } from '../root/ConfigurationContext';
import { HailRatesView } from '../hail-pricing-tool/HailRatesView'; // Import HailRatesView
import { AccountAvatar } from '../accounts/AccountAvatar'; // Add AccountAvatar import

const paymentMethods = [
    'Cash Discount (when applicable)',
    'Premium Billing Date (no discount)',
];

// Debounce Utility (same as Create)
function debounce<F extends (...args: any[]) => any>(
    func: F,
    waitFor: number
): (...args: Parameters<F>) => ReturnType<F> {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const debounced = (...args: Parameters<F>) => {
        if (timeout !== null) {
            clearTimeout(timeout);
            timeout = null;
        }
        timeout = setTimeout(() => func(...args), waitFor);
    };
    return debounced as (...args: Parameters<F>) => ReturnType<F>;
}

// Form Data Interface (same as Create)
interface QuoteFormData {
    name: string;
    description?: string | null; // Allow null from DB
    category?: string | null;
    amount?: number | null;
    sales_id?: Identifier;
    account?: string;
    accountId?: Identifier;
    contact?: string;
    contactId?: Identifier; // ID of the primary contact being edited
    contact_ids?: Identifier[]; // Keep this to match DB schema
    state?: string | null;
    county?: string | null;
    township?: string | null;
    range?: string | null;
    crop?: string | null;
    active?: boolean | null;
    selectedCarriers?: string[];
    selectedHailProducts?: string[];
    stage?: string | null;
    paymentMethod?: string | null;
    acres?: string | number | null; // Allow string for input, number for DB
    coveragePerAcre?: string | number | null;
    premiumPerAcre?: string | number | null;
    notes?: string | null;
    targetDate?: string | null;
    opportunityOwner?: string;
    opportunityOwnerId?: Identifier;
    quote_lines?: QuoteLine[];
}

export const QuoteEdit = ({
    open,
    id,
    onClose,
}: {
    open: boolean;
    id?: string;
    onClose?: () => void;
}) => {
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { identity, isLoading: identityLoading } = useGetIdentity();
    const { quoteStages, quoteCategories } = useConfigurationContext();
    const queryClient = useQueryClient();

    // --- State Management ---
    const [formData, setFormData] = useState<Partial<QuoteFormData>>({}); // Start empty
    const [originalQuote, setOriginalQuote] = useState<Quote | null>(null); // Store fetched quote
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Autocomplete States
    const [accountInputValue, setAccountInputValue] = useState('');
    const [accountOptions, setAccountOptions] = useState<readonly Account[]>(
        []
    );
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(
        null
    );
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [contactOptions, setContactOptions] = useState<readonly Contact[]>(
        []
    );
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]); // All contacts on the quote
    const [oppOwnerInputValue, setOppOwnerInputValue] = useState('');
    const [oppOwnerOptions, setOppOwnerOptions] = useState<readonly Sale[]>([]);
    const [selectedOppOwner, setSelectedOppOwner] = useState<Sale | null>(null);
    const [loadingOppOwners, setLoadingOppOwners] = useState(false);

    // Form Step State
    const [formStep, setFormStep] = useState<'initial_form' | 'show_rates'>(
        'initial_form'
    );

    // --- Data Fetching ---

    // Fetch Quote and related records on mount/id change
    useEffect(() => {
        if (!id) {
            setError(new Error('No Quote ID provided for edit.'));
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const fetchQuoteAndRelated = async () => {
            try {
                // 1. Fetch the Quote
                const { data: quoteRecord } = await dataProvider.getOne(
                    'quotes',
                    { id }
                );
                if (!quoteRecord) throw new Error('Quote not found');
                setOriginalQuote(quoteRecord); // Store for previousData

                // 2. Populate formData state
                setFormData({
                    name: quoteRecord.name,
                    description: quoteRecord.description,
                    stage: quoteRecord.stage,
                    category: quoteRecord.category,
                    amount: quoteRecord.amount,
                    sales_id: quoteRecord.sales_id,
                    accountId: quoteRecord.account_id,
                    contactId: quoteRecord.contact_ids?.[0], // Assume first contact is primary for edit
                    contact_ids: quoteRecord.contact_ids || [],
                    state: quoteRecord.state,
                    county: quoteRecord.county,
                    township: quoteRecord.township,
                    range: quoteRecord.range,
                    crop: quoteRecord.crop,
                    active: quoteRecord.active ?? true,
                    selectedCarriers: quoteRecord.selectedCarriers || [],
                    // showOnlyAppointed: false, // Doesn't seem to be saved
                    selectedHailProducts:
                        quoteRecord.selectedHailProducts || [],
                    // showDefaultProducts: false, // Doesn't seem to be saved
                    paymentMethod: quoteRecord.paymentMethod,
                    acres: quoteRecord.acres?.toString() || '', // Convert number to string for input
                    coveragePerAcre:
                        quoteRecord.coveragePerAcre?.toString() || '',
                    premiumPerAcre:
                        quoteRecord.premiumPerAcre?.toString() || '',
                    notes: quoteRecord.notes,
                    targetDate:
                        quoteRecord.targetDate ||
                        new Date().toISOString().split('T')[0],
                    opportunityOwnerId: quoteRecord.opportunityOwnerId,
                    quote_lines: quoteRecord.quote_lines ?? [],
                });

                // 3. Fetch related records for display/selection
                let fetchedAccount: Account | null = null;
                let fetchedOppOwner: Sale | null = null;

                const relatedFetches = [];
                if (quoteRecord.account_id) {
                    relatedFetches.push(
                        dataProvider.getOne('accounts_summary', {
                            id: quoteRecord.account_id,
                        })
                    );
                } else {
                    relatedFetches.push(Promise.resolve(null));
                } // Placeholder

                const contactIds = quoteRecord.contact_ids || [];
                if (contactIds.length > 0) {
                    relatedFetches.push(
                        dataProvider.getMany('contacts_summary', {
                            ids: contactIds,
                        })
                    );
                } else {
                    relatedFetches.push(Promise.resolve(null));
                } // Placeholder

                if (quoteRecord.opportunityOwnerId) {
                    relatedFetches.push(
                        dataProvider.getOne('sales', {
                            id: quoteRecord.opportunityOwnerId,
                        })
                    );
                } else {
                    relatedFetches.push(Promise.resolve(null));
                } // Placeholder

                const [accountResult, contactResult, oppOwnerResult] =
                    await Promise.allSettled(relatedFetches);

                if (
                    accountResult.status === 'fulfilled' &&
                    accountResult.value?.data
                ) {
                    fetchedAccount = accountResult.value.data;
                    if (fetchedAccount) {
                        setSelectedAccount(fetchedAccount);
                        setAccountInputValue(fetchedAccount.name);
                        // Fetch contacts for this account
                        await fetchContactsForAccount(fetchedAccount.id);
                    }
                } else if (accountResult.status === 'rejected') {
                    console.error(
                        'Failed to fetch account:',
                        accountResult.reason
                    );
                    // Optionally notify user or set error state
                }

                if (
                    contactResult.status === 'fulfilled' &&
                    contactResult.value?.data
                ) {
                    const fetchedContacts = Array.isArray(
                        contactResult.value.data
                    )
                        ? contactResult.value.data
                        : [contactResult.value.data];
                    setSelectedContacts(fetchedContacts);
                } else if (contactResult.status === 'rejected') {
                    console.error(
                        'Failed to fetch contacts:',
                        contactResult.reason
                    );
                }

                if (
                    oppOwnerResult.status === 'fulfilled' &&
                    oppOwnerResult.value?.data
                ) {
                    fetchedOppOwner = oppOwnerResult.value.data;
                    if (fetchedOppOwner) {
                        setSelectedOppOwner(fetchedOppOwner);
                        setOppOwnerInputValue(
                            `${fetchedOppOwner.first_name} ${fetchedOppOwner.last_name}`
                        );
                    }
                } else if (oppOwnerResult.status === 'rejected') {
                    console.error(
                        'Failed to fetch opportunity owner:',
                        oppOwnerResult.reason
                    );
                }

                console.log('Fetched quote and related data');
            } catch (fetchError: any) {
                console.error('Error fetching quote data:', fetchError);
                setError(fetchError);
                notify(
                    `Error fetching quote: ${fetchError.message || 'Unknown error'}`,
                    { type: 'error' }
                );
            }
            setLoading(false);
        };

        fetchQuoteAndRelated();
    }, [id, dataProvider, notify]); // Dependency on id

    // --- Autocomplete Fetchers (Mostly same as Create, ensure fetchContactsForAccount is defined early) ---
    const fetchContactsForAccount = useCallback(
        async (accountId: Identifier | undefined) => {
            if (!accountId) {
                setContactOptions([]);
                return;
            }
            setLoadingContacts(true);
            // Don't reset selected contact here in edit mode initially
            try {
                const { data } = await dataProvider.getList(
                    'contacts_summary',
                    {
                        pagination: { page: 1, perPage: 100 },
                        sort: { field: 'last_name', order: 'ASC' },
                        filter: { account_id: accountId },
                    }
                );
                setContactOptions(data || []);
            } catch (error) {
                console.error(
                    `Error fetching contacts for account ${accountId}:`,
                    error
                );
                setContactOptions([]);
                notify('Error fetching contacts', { type: 'error' });
            }
            setLoadingContacts(false);
        },
        [dataProvider, notify]
    );

    const debouncedFetchAccounts = useMemo(
        () =>
            debounce(async (inputValue: string) => {
                if (!inputValue || inputValue.length < 2) {
                    setAccountOptions([]);
                    setLoadingAccounts(false);
                    return;
                }
                setLoadingAccounts(true);
                try {
                    const { data } = await dataProvider.getList(
                        'accounts_summary',
                        {
                            pagination: { page: 1, perPage: 15 },
                            sort: { field: 'name', order: 'ASC' },
                            filter: { 'name@ilike': `%${inputValue}%` },
                        }
                    );
                    // Ensure the currently selected account is in the options if user searches
                    const currentAccountId = selectedAccount?.id;
                    const options = data || [];
                    if (
                        currentAccountId &&
                        !options.some(opt => opt.id === currentAccountId)
                    ) {
                        // If selected account not in results, fetch it and add it
                        try {
                            const { data: currentAcc } =
                                await dataProvider.getOne('accounts_summary', {
                                    id: currentAccountId,
                                });
                            if (currentAcc) options.unshift(currentAcc); // Add to beginning
                        } catch {
                            /* ignore error if selected acc fetch fails */
                        }
                    }
                    setAccountOptions(options);
                } catch (error) {
                    console.error('Error fetching accounts:', error);
                    setAccountOptions([]);
                    notify('Error fetching accounts', { type: 'error' });
                }
                setLoadingAccounts(false);
            }, 500),
        [dataProvider, notify, selectedAccount]
    );

    useEffect(() => {
        // Fetch accounts only if input value changes (don't fetch on initial load based on pre-filled value)
        if (accountInputValue !== selectedAccount?.name) {
            debouncedFetchAccounts(accountInputValue);
        }
    }, [accountInputValue, debouncedFetchAccounts, selectedAccount]);

    const debouncedFetchOppOwners = useMemo(
        () =>
            debounce(async (inputValue: string) => {
                if (!inputValue || inputValue.length < 1) {
                    setOppOwnerOptions([]);
                    setLoadingOppOwners(false);
                    return;
                }
                setLoadingOppOwners(true);
                try {
                    const filter = {
                        '@or': {
                            'first_name@ilike': `%${inputValue}%`,
                            'last_name@ilike': `%${inputValue}%`,
                        },
                    };
                    const { data } = await dataProvider.getList('sales', {
                        pagination: { page: 1, perPage: 15 },
                        sort: { field: 'last_name', order: 'ASC' },
                        filter: filter,
                    });
                    // Ensure selected owner is in options
                    const currentOwnerId = selectedOppOwner?.id;
                    const options = data || [];
                    if (
                        currentOwnerId &&
                        !options.some(opt => opt.id === currentOwnerId)
                    ) {
                        try {
                            const { data: currentOwner } =
                                await dataProvider.getOne('sales', {
                                    id: currentOwnerId,
                                });
                            if (currentOwner) options.unshift(currentOwner);
                        } catch {
                            /* ignore error */
                        }
                    }
                    setOppOwnerOptions(options);
                } catch (error) {
                    console.error('Error fetching opportunity owners:', error);
                    setOppOwnerOptions([]);
                    notify('Error fetching opportunity owners', {
                        type: 'error',
                    });
                }
                setLoadingOppOwners(false);
            }, 500),
        [dataProvider, notify, selectedOppOwner]
    );

    useEffect(() => {
        const currentOwnerName = selectedOppOwner
            ? `${selectedOppOwner.first_name} ${selectedOppOwner.last_name}`
            : '';
        if (oppOwnerInputValue !== currentOwnerName) {
            debouncedFetchOppOwners(oppOwnerInputValue);
        }
    }, [oppOwnerInputValue, debouncedFetchOppOwners, selectedOppOwner]);

    // --- Input Handlers (same as Create) ---
    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        let parsedValue: string | number | boolean | null = value;
        if (type === 'checkbox' || type === 'switch') {
            parsedValue = checked;
        } else if (type === 'number') {
            parsedValue = value === '' ? null : parseFloat(value);
        }
        setFormData(prevData => ({
            ...prevData,
            [name]: parsedValue,
        }));
    };

    const handleSelectChange = (e: SelectChangeEvent<string | string[]>) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
            ...(name === 'state' && { county: '' }),
        }));
    };

    // --- Other Functions ---
    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            redirect(`/quotes/${id}/page`);
        }
    };

    const handleGetRates = () => {
        console.log('Get/Update Rates Clicked. Form Data:', formData);
        setFormStep('show_rates');
    };

    const handleSaveQuote = async () => {
        if (!id || !originalQuote) {
            notify('Cannot save quote: ID or original data missing.', {
                type: 'error',
            });
            return;
        }
        console.log('Save Changes Clicked. Preparing data:', formData);

        const dataToSave: Partial<Quote> & { [key: string]: any } = {
            // Map formData fields, providing defaults for null/undefined where necessary
            name: formData.name || '', // Ensure string
            description: formData.description ?? undefined, // Use undefined instead of null
            category: formData.category ?? undefined,
            stage: formData.stage ?? undefined,
            amount: formData.amount ?? 0,
            account_id: formData.accountId,
            contact_ids: formData.contact_ids?.length
                ? formData.contact_ids
                : originalQuote.contact_ids || [],
            sales_id: formData.sales_id,
            state: formData.state ?? undefined,
            county: formData.county ?? undefined,
            township: formData.township ?? undefined,
            range: formData.range ?? undefined,
            crop: formData.crop ?? undefined,
            active: formData.active ?? true,
            selectedCarriers: formData.selectedCarriers || [],
            selectedHailProducts: formData.selectedHailProducts || [],
            paymentMethod: formData.paymentMethod ?? undefined,
            acres: parseFloat(formData.acres?.toString() || '0'),
            coveragePerAcre: parseFloat(
                formData.coveragePerAcre?.toString() || '0'
            ),
            premiumPerAcre: parseFloat(
                formData.premiumPerAcre?.toString() || '0'
            ),
            targetDate: formData.targetDate ?? undefined, // Use undefined instead of null
            opportunityOwnerId: formData.opportunityOwnerId,
            notes: formData.notes ?? undefined, // Use undefined instead of null
            quote_lines:
                formData.quote_lines ?? originalQuote.quote_lines ?? [],
            updated_at: new Date().toISOString(),
        };

        // Clean up undefined/null/empty values - simplified: send all mapped fields
        // Let the backend handle defaults or ignore nulls if appropriate.
        /* 
         Object.keys(dataToSave).forEach(key => 
            (dataToSave[key] === undefined || dataToSave[key] === null || dataToSave[key] === '') && 
            key !== 'amount' && // Allow 0 amount
            delete dataToSave[key]
        );
        */

        console.log('Attempting to update quote with data:', dataToSave);

        try {
            await dataProvider.update('quotes', {
                id: id,
                data: dataToSave,
                previousData: originalQuote,
            });
            notify('Quote updated successfully!', { type: 'success' });
            queryClient.invalidateQueries({
                queryKey: ['quotes', 'getOne', { id }],
            });
            queryClient.invalidateQueries({ queryKey: ['quotes', 'getList'] });
            if (onClose) {
                onClose();
            } else {
                redirect(`/quotes/${id}/page`);
            }
        } catch (error: any) {
            console.error('Error updating quote:', error);
            notify(
                `Error updating quote: ${error.message || 'Unknown error'}`,
                { type: 'error' }
            );
        }
    };

    // --- Render Logic ---
    if (loading) {
        return (
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Edit Quote</DialogTitle>
                <DialogContent>
                    <CircularProgress sx={{ m: 2 }} />
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <Alert severity="error">
                        {error.message || 'Could not load quote data.'}
                    </Alert>
                </DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Button onClick={handleClose}>Close</Button>
                </Box>
            </Dialog>
        );
    }

    if (!originalQuote) {
        // Should not happen if loading/error handled, but safety check
        return (
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Quote not found</DialogTitle>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { overflowY: 'unset' } }}
        >
            <DialogTitle
                sx={{
                    m: 0,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Stack direction="row" alignItems="center" gap={2}>
                    <ReferenceField
                        record={originalQuote}
                        source="account_id"
                        reference="accounts"
                        link="show"
                    >
                        <AccountAvatar />
                    </ReferenceField>
                    <Typography variant="h6">
                        Edit {originalQuote.name} quote
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        component={RouterLink}
                        to={`/quotes/${id}/page`}
                        size="small"
                    >
                        Back to quote
                    </Button>
                    <DialogCloseButton onClose={handleClose} />
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {formStep === 'initial_form' && (
                    <Box
                        component="form"
                        noValidate
                        autoComplete="off"
                        sx={{ p: 3 }}
                    >
                        {/* Form content - same structure as Create, but uses formData state */}
                        {/* Quote Details */}
                        <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{ fontWeight: 'medium' }}
                        >
                            Quote Details
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Quote Name"
                                    name="name"
                                    value={formData.name || ''}
                                    onChange={handleInputChange}
                                    size="small"
                                    required
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Description"
                            name="description"
                            value={formData.description || ''}
                            onChange={handleInputChange}
                            placeholder="Optional description..."
                            size="small"
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Opportunity Stage</InputLabel>
                            <Select
                                name="stage"
                                value={formData.stage || ''}
                                onChange={(e: SelectChangeEvent) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        stage: e.target.value,
                                    }));
                                }}
                                label="Opportunity Stage"
                            >
                                {quoteStages.map(s => (
                                    <MenuItem key={s.value} value={s.value}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 3 }} />

                        {/* Account Info */}
                        <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{ fontWeight: 'medium' }}
                        >
                            Account Information
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    id="account-autocomplete"
                                    options={accountOptions}
                                    loading={loadingAccounts}
                                    value={selectedAccount}
                                    inputValue={accountInputValue}
                                    onInputChange={(event, newInputValue) =>
                                        setAccountInputValue(newInputValue)
                                    }
                                    onChange={(
                                        event,
                                        newValue: Account | null
                                    ) => {
                                        setSelectedAccount(newValue);
                                        setFormData(prev => ({
                                            ...prev,
                                            account: newValue
                                                ? newValue.name
                                                : '',
                                            accountId: newValue
                                                ? newValue.id
                                                : undefined,
                                            contact: '', // Reset contact
                                            contactId: undefined,
                                            contact_ids: [],
                                        }));
                                        setSelectedContacts([]); // Reset selected contacts
                                        fetchContactsForAccount(newValue?.id);
                                    }}
                                    getOptionLabel={option => option.name || ''}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    filterOptions={x => x}
                                    fullWidth
                                    size="small"
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Account"
                                            required
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingAccounts ? (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        ) : null}
                                                        {
                                                            params.InputProps
                                                                .endAdornment
                                                        }
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    multiple
                                    id="contacts-autocomplete"
                                    options={contactOptions as Contact[]}
                                    loading={loadingContacts}
                                    value={selectedContacts}
                                    disabled={
                                        !selectedAccount || loadingContacts
                                    }
                                    onChange={(_event, newValue: Contact[]) => {
                                        setSelectedContacts(newValue);
                                        setFormData(prev => ({
                                            ...prev,
                                            contact_ids: newValue.map(
                                                c => c.id
                                            ),
                                        }));
                                    }}
                                    getOptionLabel={option =>
                                        `${option.first_name} ${option.last_name}`
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    size="small"
                                    fullWidth
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Contacts"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingContacts ? (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={20}
                                                            />
                                                        ) : null}
                                                        {
                                                            params.InputProps
                                                                .endAdornment
                                                        }
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* Policy Info (same as Create) */}
                        <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{ fontWeight: 'medium' }}
                        >
                            Policy Information
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel
                                        id="payment-method-label"
                                        required
                                    >
                                        Payment Method
                                    </InputLabel>
                                    <Select
                                        labelId="payment-method-label"
                                        name="paymentMethod"
                                        value={formData.paymentMethod || ''}
                                        label="Payment Method"
                                        onChange={handleSelectChange}
                                        required
                                    >
                                        {paymentMethods.map(method => (
                                            <MenuItem
                                                key={method}
                                                value={method}
                                            >
                                                {method}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Amount"
                                    name="amount"
                                    type="number"
                                    value={
                                        formData.amount != null
                                            ? formData.amount
                                            : ''
                                    }
                                    onChange={handleInputChange}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                $
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* Target Date & Opp Owner (same as Create) */}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Target Date"
                                    name="targetDate"
                                    type="date"
                                    value={formData.targetDate || ''}
                                    onChange={handleInputChange}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                >
                                    <Autocomplete
                                        id="opp-owner-autocomplete"
                                        options={oppOwnerOptions}
                                        loading={
                                            loadingOppOwners || identityLoading
                                        }
                                        value={selectedOppOwner}
                                        inputValue={oppOwnerInputValue}
                                        onInputChange={(event, newInputValue) =>
                                            setOppOwnerInputValue(newInputValue)
                                        }
                                        onChange={(
                                            event,
                                            newValue: Sale | null
                                        ) => {
                                            setSelectedOppOwner(newValue);
                                            setFormData(prev => ({
                                                ...prev,
                                                opportunityOwner: newValue
                                                    ? `${newValue.first_name} ${newValue.last_name}`
                                                    : '',
                                                opportunityOwnerId:
                                                    newValue?.id,
                                            }));
                                        }}
                                        getOptionLabel={option =>
                                            `${option.first_name} ${option.last_name}` ||
                                            ''
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value.id
                                        }
                                        filterOptions={x => x}
                                        fullWidth
                                        size="small"
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Opportunity Owner"
                                                required
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {loadingOppOwners ||
                                                            identityLoading ? (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            ) : null}
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                        </React.Fragment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    <Chip
                                        label="Me"
                                        size="small"
                                        color={
                                            selectedOppOwner?.id ===
                                            identity?.id
                                                ? 'primary'
                                                : 'default'
                                        }
                                        onClick={async () => {
                                            if (!identity?.id) return;
                                            try {
                                                const { data: mySale } =
                                                    await dataProvider.getOne(
                                                        'sales',
                                                        {
                                                            id: identity.id,
                                                        }
                                                    );
                                                if (mySale) {
                                                    setSelectedOppOwner(mySale);
                                                    setOppOwnerInputValue(
                                                        `${mySale.first_name} ${mySale.last_name}`
                                                    );
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        opportunityOwner: `${mySale.first_name} ${mySale.last_name}`,
                                                        opportunityOwnerId:
                                                            mySale.id,
                                                    }));
                                                }
                                            } catch (err) {
                                                console.error(
                                                    'Failed to fetch current user sales record:',
                                                    err
                                                );
                                            }
                                        }}
                                        sx={{
                                            mt: '4px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>

                        {/* Action Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mt: 3,
                                gap: 1,
                            }}
                        >
                            <Button variant="text" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSaveQuote}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                )}

                {formStep === 'show_rates' && (
                    <HailRatesView
                        formData={formData}
                        onBack={() => setFormStep('initial_form')}
                        onSave={handleSaveQuote} // Save handler now calls dataProvider.update
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
